import { useTranslation } from "react-i18next";

const getServices = (t: (key: string) => string) => [
  { icon: "ri-community-line", label: t("tenderService1"), desc: t("tenderService1Desc") },
  { icon: "ri-file-search-line", label: t("tenderService2"), desc: t("tenderService2Desc") },
  { icon: "ri-pencil-ruler-2-line", label: t("tenderService3"), desc: t("tenderService3Desc") },
  { icon: "ri-shield-check-line", label: t("tenderService4"), desc: t("tenderService4Desc") },
  { icon: "ri-bar-chart-line", label: t("tenderService5"), desc: t("tenderService5Desc") },
  { icon: "ri-safe-2-line", label: t("tenderService6"), desc: t("tenderService6Desc") },
  { icon: "ri-award-line", label: t("tenderService7"), desc: t("tenderService7Desc") },
];

export default function TendersHero() {
  const { t } = useTranslation();
  const services = getServices(t);

  return (
    <section id="page-hero" data-dark-section className="relative min-h-screen flex items-center overflow-hidden bg-[#0A1628]">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=government%20public%20procurement%20tender%20contract%20signing%20ceremony%20in%20modern%20East%20African%20government%20office%2C%20professional%20executives%20reviewing%20official%20bid%20documents%2C%20dark%20moody%20dramatic%20lighting%20with%20cool%20blue%20accent%20tones%2C%20contemporary%20architecture&width=1920&height=1080&seq=tender-hero-v2&orientation=landscape"
          alt="Tender support services"
          className="w-full h-full object-cover object-top opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/90 via-[#0A1628]/70 to-[#0A1628]/30" />
      </div>

      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 right-1/3 w-80 h-80 rounded-full bg-[#0A1628]/8 blur-3xl" />
        <div className="absolute bottom-1/3 right-10 w-48 h-48 rounded-full bg-[#0A1628]/6 blur-2xl" />
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-40 bg-[#0A1628] rounded-r-full" />
        <div className="absolute top-0 left-0 w-full h-full opacity-5">
          <div className="w-full h-full" style={{ backgroundImage: "linear-gradient(to right, #0A1628 1px, transparent 1px), linear-gradient(to bottom, #0A1628 1px, transparent 1px)", backgroundSize: "80px 80px" }} />
        </div>
      </div>

      <div className="relative z-10 w-full px-6 lg:px-12 pt-32 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[calc(100vh-120px)]">
          <div className="flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 bg-[#0A1628]/15 border border-[#0A1628]/30 text-[#00C2C2] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6 w-fit">
              <span className="block w-1.5 h-1.5 rounded-full bg-[#0A1628] animate-pulse" />
              {t("tenderHeroTag")}
            </div>

            <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-6">
              {t("tenderHeroTitle1")}<br />
              <span className="text-[#00C2C2]" style={{ fontStyle: "italic", fontWeight: 400 }}>
                {t("tenderHeroTitle2")}
              </span>
            </h1>

            <p className="text-white/60 text-lg leading-relaxed max-w-xl mb-8">
              {t("tenderHeroDesc")}
            </p>

            <div className="flex flex-wrap gap-3 mb-12">
              <a
                href="#tender-list"
                className="bg-[#0A1628] text-white text-sm font-bold px-7 py-3.5 rounded-full hover:bg-[#0A1628] transition-all whitespace-nowrap cursor-pointer"
              >
                {t("tenderCtaListings")}
              </a>
              <a
                href="#tender-form"
                className="bg-white/10 border border-white/25 text-white text-sm font-semibold px-7 py-3.5 rounded-full hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
              >
                {t("tenderCtaRequest")}
              </a>
            </div>

            <div className="flex flex-wrap gap-6 pt-8 border-t border-white/10">
              {[
                { value: "150+", label: t("tenderStat1") },
                { value: "12", label: t("tenderStat2") },
                { value: "6", label: t("tenderStat3") },
                { value: "24h", label: t("tenderStat4") },
              ].map((s) => (
                <div key={s.label}>
                  <div className="text-2xl font-bold text-white">{s.value}</div>
                  <div className="text-white/40 text-xs mt-0.5 tracking-wide">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:flex flex-col gap-3">
            <p className="text-white/40 text-xs font-semibold uppercase tracking-widest mb-2">{t("tenderServicesTitle")}</p>
            {services.map((s, i) => (
              <div
                key={s.label}
                className="group flex items-start gap-4 bg-white/5 border border-white/10 rounded-xl p-5 hover:border-[#00C2C2]/40 hover:bg-white/8 transition-all cursor-default"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#0A1628]/15 rounded-lg shrink-0 group-hover:bg-[#0A1628]/25 transition-colors">
                  <i className={`${s.icon} text-lg text-[#00C2C2]`} />
                </div>
                <div>
                  <p className="text-white font-bold text-sm mb-0.5">{s.label}</p>
                  <p className="text-white/50 text-xs leading-relaxed">{s.desc}</p>
                </div>
                <span className="ml-auto text-white/20 text-lg font-bold shrink-0">0{i + 1}</span>
              </div>
            ))}

            <div className="mt-2 flex items-center gap-3 bg-[#0A1628]/10 border border-[#0A1628]/25 rounded-xl p-4">
              <div className="w-8 h-8 flex items-center justify-center shrink-0">
                <i className="ri-verified-badge-line text-xl text-[#00C2C2]" />
              </div>
              <p className="text-white/70 text-xs leading-relaxed">
                {t("tenderTrustBadge")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}